import { io } from 'socket.io-client'
import { getAuthToken } from '../http'
import { toast } from 'react-toastify'

class SocketClient {
    constructor() {
        this.socket = null
        this.isConnected = false
        this.reconnectAttempts = 0
        this.maxReconnectAttempts = 5
        this.eventListeners = new Map()
    }

    connect() {
        // Проверяем есть ли уже активное соединение
        if (this.socket && this.socket.connected) {
            console.log('✅ Socket already connected, skipping')
            return
        }

        // Если есть socket но он не connected - отключаем его
        if (this.socket) {
            console.log(
                '🔄 Existing socket found, disconnecting before reconnect'
            )
            this.socket.disconnect()
            this.socket = null
        }

        const token = getAuthToken()

        if (!token) {
            console.warn('No auth token found, skipping socket connection')
            return
        }

        // Определяем server URL: env переменная или текущий домен
        const serverUrl =
            process.env.REACT_APP_SOCKET_URL || window.location.origin

        console.log('🔌 Connecting to socket server:', serverUrl, {
            fromEnv: !!process.env.REACT_APP_SOCKET_URL,
            windowOrigin: window.location.origin,
        })

        this.socket = io(serverUrl, {
            auth: {
                token: token,
            },
            path: '/socket.io',
            // Сначала WebSocket (быстрее), fallback на polling если WebSocket заблокирован
            transports: ['websocket', 'polling'],
            upgrade: true,
            autoConnect: true,
            reconnection: true,
            reconnectionAttempts: this.maxReconnectAttempts,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000,
            // Для Cloudflare - увеличенные таймауты
            pingTimeout: 30000,
            pingInterval: 25000,
        })

        this.setupEventListeners()
    }

    setupEventListeners() {
        if (!this.socket) return

        this.socket.on('connect', () => {
            console.log('✅ Connected to WebSocket server')
            this.isConnected = true
            this.reconnectAttempts = 0
            this.emit('socket_connected')
        })

        this.socket.on('disconnect', (reason) => {
            console.log('❌ Disconnected from WebSocket server:', reason)
            this.isConnected = false
            this.emit('socket_disconnected', reason)
        })

        this.socket.on('connect_error', (error) => {
            console.error('❌ Socket connection error:', error)
            this.reconnectAttempts++
            this.emit('socket_error', error)
        })

        this.socket.on('reconnect', (attemptNumber) => {
            console.log(
                `✅ Reconnected to WebSocket server (attempt ${attemptNumber})`
            )
            this.isConnected = true
            this.emit('socket_reconnected')
        })

        this.socket.on('reconnect_failed', () => {
            console.error('❌ Failed to reconnect to WebSocket server')
            this.emit('socket_reconnect_failed')
        })

        this.socket.on('notification', (data) => {
            this.emit('notification', data)
        })

        this.socket.on('balance_updated', (data) => {
            this.emit('balance_updated', data)
        })

        this.socket.on('energy_updated', (data) => {
            this.emit('energy_updated', data)
        })

        this.socket.on('battle_started', (data) => {
            console.log('🔥 Socket received battle_started:', data)
            this.emit('battle_started', data)
        })

        this.socket.on('battle_finished', (data) => {
            console.log('🔥 Socket received battle_finished:', data)
            this.emit('battle_finished', data)
        })

        this.socket.on('pong', (data) => {
            this.emit('pong', data)
        })

        this.socket.on('new_promo', (data) => {
            this.emit('new_promo', data)
        })

        this.socket.on('user_banned', (data) => {
            console.warn('User banned:', data)
            const banToastShown = sessionStorage.getItem('banToastShown')
            if (!banToastShown) {
                import('../i18n').then(({ default: i18n }) => {
                    const reason = data.reason || i18n.t('common.banned.reason')
                    toast(`${i18n.t('common.banned.title')}: ${reason}`, {
                        type: 'error',
                    })
                })
                sessionStorage.setItem('banToastShown', 'true')
            }
            this.emit('user_banned', data)
        })

        this.socket.on('task_progress_updated', (data) => {
            console.log('📊 Task progress updated:', data)
            this.emit('task_progress_updated', data)
        })

        this.socket.on('game_action', (data) => {
            console.log('🎮 Game action:', data)
            this.emit('game_action', data)
        })

        this.socket.on('referral_added', (data) => {
            console.log('👥 Referral added:', data)
            this.emit('referral_added', data)
        })

        this.socket.on('leaderboard_reward', (data) => {
            console.log('🏆 Leaderboard reward:', data)
            this.emit('leaderboard_reward', data)
        })
    }

    disconnect() {
        if (this.socket) {
            this.socket.disconnect()
            this.socket = null
            this.isConnected = false
        }
    }

    on(event, callback) {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, [])
        }
        this.eventListeners.get(event).push(callback)
    }

    off(event, callback) {
        if (this.eventListeners.has(event)) {
            const listeners = this.eventListeners.get(event)
            const index = listeners.indexOf(callback)
            if (index > -1) {
                listeners.splice(index, 1)
            }
        }
    }

    emit(event, data) {
        if (this.eventListeners.has(event)) {
            this.eventListeners.get(event).forEach((callback) => {
                try {
                    callback(data)
                } catch (error) {
                    console.error(
                        `Error in socket event handler for ${event}:`,
                        error
                    )
                }
            })
        }
    }

    send(event, data) {
        if (this.socket && this.isConnected) {
            this.socket.emit(event, data)
        } else {
            console.warn('Socket not connected, cannot send event:', event)
        }
    }

    joinBattle(battleId) {
        console.log('🎮 Joining battle room:', battleId)
        this.send('join_battle', { battleId })
    }

    leaveBattle(battleId) {
        console.log('🚪 Leaving battle room:', battleId)
        this.send('leave_battle', { battleId })
    }

    ping() {
        this.send('ping')
    }

    updateStatus(status, activity) {
        this.send('update_status', { status, activity })
    }

    getOnlineUsers() {
        this.send('get_online_users')
    }

    isSocketConnected() {
        return this.isConnected && this.socket?.connected
    }
}

const socketClient = new SocketClient()

export default socketClient
